import mixpanel from 'mixpanel-browser';
import { clerk } from './clerkConfig';
import { getOrSetDiffitSessionId } from '../utils/diffitSessionId';

const standardProps = () => {
  let props = {};

  // eslint-disable-next-line no-undef
  const commitSha = __VITE_VERCEL_COMMIT_SHA__;
  // eslint-disable-next-line no-undef
  const buildTime = __VITE_BUILD_TIME__;

  if (window) {
    props.viewportWidth = window.innerWidth;
    props.viewportHeight = window.innerHeight;
  }
  if (commitSha) {
    props.vercelReleaseCommitSha = commitSha;
    window.__VITE_VERCEL_COMMIT_SHA__ = commitSha;
  }
  if (buildTime) {
    props.releaseBuildTime = buildTime;
    window.__VITE_BUILD_TIME__ = buildTime;
  }
  // `clerk.session` is undefined until clerk.load() resolves (~1.2s on a cold
  // load). Mixpanel init no longer waits on Clerk, so events can fire before
  // then — and asserting `false` in that window would mis-stamp a signed-in
  // user's first 'Page Viewed' as logged out, inflating exactly the logged-out
  // population this work measures. Omit the property instead: absent means
  // "not known yet", which is recoverable downstream; `false` is just wrong.
  if (clerk.loaded) {
    props.isLoggedIn = !!clerk.session;
  }
  return props;
};

// Register the LaunchDarkly bucketing key (diffitSessionId) as a Mixpanel
// super-prop so every event carries LD's actual bucketing unit. Same value used
// for the LD-Session header (see baseXhrHeaders below), so ld_session_id matches
// LD 1:1. mixpanel.reset() clears super-props, so this runs on init AND is
// re-called right after every reset() (see below).
//
// Creates the id if it doesn't exist yet rather than reading localStorage
// directly. Init now runs at module scope, which is BEFORE anything else has had
// a chance to create it — a bare read would no-op on a first-ever visit and
// leave that whole session with no ld_session_id, i.e. no join key back to LD
// for precisely the anonymous cohort this work is trying to attribute.
const registerSessionIdToMixpanel = () => {
  mixpanel.register({ ld_session_id: getOrSetDiffitSessionId() });
};

// The SDK holds xhr_headers as a plain object captured when it was last set, so
// this is re-applied on every wrapped call (see initMixpanel) — country_code is
// resolved by an async geo lookup and lands after Mixpanel is already up.
//
// No Authorization header: the /mixpanel reverse proxy is a no_auth route that
// drops every inbound header except X-REAL-IP before forwarding, and the one
// path that does forward `authorization` (/record) already defaults it to '' for
// the logged-out visitors who make up most session replays. So a Clerk bearer
// token here has no consumer, and fetching one is what used to put clerk.load()
// on the critical path for $device_id, experiment bucketing and flag attribution.
const baseXhrHeaders = () => ({
  'LD-Session': getOrSetDiffitSessionId(),
  'X-Country-Code': localStorage.getItem('country_code'),
});

// Initialize Mixpanel. SYNCHRONOUS and idempotent. $device_id is registered
// inside mixpanel.init() and is the LaunchDarkly bucketing attribute, so it must
// exist before the LD provider builds its first context — i.e. before React
// renders at all. Returns whether the SDK is usable so callers don't fire
// methods at an uninitialized instance.
// The export pipeline (diffit-to-slides) scrapes the rendered /p/:id page in
// headless Chromium, which boots this app and would otherwise register a
// brand-new anonymous user on every export — measured at ~30% of all counted
// unique users in Mixpanel. It spoofs a desktop Chrome UA, so that traffic can't
// be filtered downstream by user agent; instead it seeds `diffitBot` in
// localStorage before the first navigation and we short-circuit on it here.
//
// The check belongs in initMixpanel() rather than on `env_check` alone: main.jsx
// calls initMixpanel() directly at module scope for $device_id, which bypasses
// the env_check gate on the action wrappers. Refusing to init at all is what
// keeps a bot session from arming session replay or registering ld_session_id.
//
// Consequence for LaunchDarkly, and it is the desired one: with no init there is
// no $device_id, so main.jsx omits mpDeviceId from the context and every scrape
// hashes to bucket 0.0 — the FIRST variation of any mpDeviceId-bucketed rollout
// (see the bucketing note in routes/Base/base.jsx). That pins exports to the
// control arm. Previously each scrape minted a fresh random $device_id, so the
// same packet could export with different experiment variations on consecutive
// runs; flags are read inside the exported render tree (e.g. CustomActivity
// GraphicSection, CustomActivityV2 ActivityImage), so that was visible in the
// output. Deterministic exports are worth more than experiment coverage on a
// surface that has no human viewer to experiment on.
//
// Read once at module load rather than per call: the scraper seeds the key via
// storageState before the first navigation, so it is present by the time this
// module is imported, and it cannot change mid-session. initMixpanel() runs on
// every one of the ~100 wrapped call sites, so this avoids a localStorage read
// on each of them.
export const IS_BOT_SESSION = (() => {
  try {
    return localStorage.getItem('diffitBot') === 'true';
  } catch {
    // localStorage can throw on access (e.g. blocked third-party storage).
    // Fail open — dropping real traffic is worse than keeping bot traffic.
    // This runs at module scope, so an uncaught throw here would break app
    // boot rather than just analytics.
    return false;
  }
})();

let initialized = false;
export function initMixpanel() {
  if (IS_BOT_SESSION) return false;

  if (initialized) {
    // Re-apply the outbound headers on every call. country_code lands from an
    // async lookup well after init, and set_config is the only way to get it
    // onto subsequent requests — the backend reads X-Country-Code and LD-Session
    // off each request into logging context vars.
    mixpanel.set_config({ xhr_headers: baseXhrHeaders() });
    return true;
  }

  try {
    mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN, {
      api_host: import.meta.env.VITE_API_BASE_URL + '/mixpanel',
      xhr_headers: baseXhrHeaders(),
      autocapture: {
        click: false,
        input: false,
        submit: false,
        scroll: true,
        scroll_depth_percent_checkpoints: [50, 100],
      },
      record_sessions_percent: 1,
      record_heatmap_data: true,
      record_idle_timeout_ms: 1800000, // End a replay capture after 30mins of inactivity
      record_mask_text_selector: '', // Show all text in session replays
    });
    initialized = true;
  } catch (err) {
    // Leave `initialized` false so the next Mixpanel call retries.
    console.error('Mixpanel init failed', err);
    return false;
  }

  registerSessionIdToMixpanel();
  return true;
}

// Wrap Mixpanel functions to ensure headers are updated on each call.
// The wrappers stay `async` so the ~100 existing `await Mixpanel.x()` call sites
// are unaffected, but initMixpanel() is now synchronous, so nothing here waits
// on a network round trip before touching the SDK.
let env_check = true;

let actions = {
  identify: async (id) => {
    if (env_check && initMixpanel()) {
      mixpanel.identify(id);
    }
  },
  alias: async (id) => {
    if (env_check && initMixpanel()) {
      mixpanel.alias(id);
    }
  },
  register: async (props) => {
    if (env_check && initMixpanel()) {
      mixpanel.register(props);
    }
  },
  unregister: async (names) => {
    if (env_check && initMixpanel()) {
      for (const name of names) mixpanel.unregister(name);
    }
  },
  // Sync super-properties atomically. SYNCHRONOUS on purpose: it has no
  // suspension point at all, so a caller running in useLayoutEffect is
  // guaranteed to have the props on the SDK before any component's useEffect
  // can fire its first track(). The previous async form relied on both sides
  // awaiting the same memoized init promise and the microtask queue preserving
  // order — true in practice, but a coincidence rather than a guarantee.
  // Callers may still `await` this; awaiting a non-promise is a no-op.
  replaceSuperProps: ({ register = {}, unregister = [] }) => {
    if (env_check && initMixpanel()) {
      for (const name of unregister) mixpanel.unregister(name);
      if (Object.keys(register).length > 0) mixpanel.register(register);
    }
  },
  // Rotates the distinct_id onto a fresh anonymous identity. Currently has NO
  // callers: sign-out deliberately preserves the user's identity so a returning
  // teacher isn't counted as a new anonymous visitor. Kept because it's the only
  // correct way to hand the browser to a genuinely different person — but note
  // that calling it is destructive under Original ID Merge, and that sign-out
  // now relies on the post-sign-out navigation (not this function) to discard
  // the stale bearer token in xhr_headers.
  reset: async () => {
    if (env_check && initMixpanel()) {
      mixpanel.reset();
      // reset() clears super-props, so re-stamp ld_session_id.
      registerSessionIdToMixpanel();
    }
  },
  track: async (name, props) => {
    if (env_check && initMixpanel()) {
      const propsToSend = {
        ...props,
        ...standardProps(),
      };
      mixpanel.track(name, propsToSend);
      // Dev-only echo of every tracked event, so payloads can be eyeballed
      // without opening Mixpanel. Excluded from prod builds.
      if (import.meta.env.DEV && name !== 'Page Viewed') {
        console.log(
          '[Mixpanel Event]\n\n',
          `${name}`,
          Object.keys(propsToSend || {}).length ? { ...propsToSend } : ''
        );
      }
    }
  },
  getDistinctId: async () => {
    // This is for anonymous user tracking
    if (env_check && initMixpanel()) {
      return mixpanel.get_distinct_id();
    }
  },
  // The Mixpanel $device_id — the unit Mixpanel counts unique users by, and
  // therefore the unit LaunchDarkly experiment flags bucket on (bucketBy:
  // mpDeviceId). Deliberately NOT get_distinct_id(): the distinct id is
  // `$device:<uuid>` while anonymous but becomes the Diffit user id after
  // identify(), so bucketing on it would re-bucket a visitor at login. The
  // device id is registered synchronously inside mixpanel.init(), survives
  // identify(), and rotates only on reset().
  getDeviceId: async () => {
    if (env_check && initMixpanel()) {
      return mixpanel.get_property('$device_id');
    }
  },
  // Synchronous form, for the LaunchDarkly provider's initial context — built at
  // module scope, before React renders, so it cannot await anything.
  getDeviceIdSync: () => {
    if (env_check && initMixpanel()) {
      return mixpanel.get_property('$device_id');
    }
    return null;
  },
  startSessionRecording: async () => {
    if (env_check && initMixpanel()) {
      mixpanel.start_session_recording();
    }
  },
  people: {
    set: async (props) => {
      if (env_check && initMixpanel()) {
        mixpanel.people.set(props);
      }
    },
    set_once: async (props) => {
      if (env_check) {
        await initMixpanel();
        mixpanel.people.set_once(props);
      }
    },
  },
};

export let Mixpanel = actions;
